import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, Flex, HStack, VStack, Text, IconButton, Switch, Badge,
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  Divider, Spinner, Tooltip, Portal,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getConnectorLogo } from '../utils/connectorLogos';
import { sortConnectorsByAvailability } from '../utils/connectorSorting';
import GitHubPlanBindingModal from './GitHubPlanBindingModal';

// ── Shared helpers ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Text
    fontSize="10px" fontWeight="700" textTransform="uppercase"
    letterSpacing="wider" color="gray.400" px={3} pt={2} pb={1}
  >
    {children}
  </Text>
);

const MenuRow = React.forwardRef(({ icon, label, meta, control, onClick, disabled, isActive, ...rest }, ref) => (
  <Flex
    ref={ref}
    align="center" justify="space-between"
    px={3} py={2} borderRadius="lg"
    cursor={disabled ? 'default' : 'pointer'}
    opacity={disabled ? 0.5 : 1}
    bg={isActive ? 'gray.50' : 'transparent'}
    _hover={disabled ? {} : { bg: 'gray.50' }}
    onClick={disabled ? undefined : onClick}
    userSelect="none"
    {...rest}
  >
    <HStack spacing={3} minW={0}>
      {icon && (
        <Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.400" flexShrink={0}>
          {icon}
        </Box>
      )}
      <Text fontSize="sm" color="gray.700" fontWeight="500">{label}</Text>
    </HStack>
    <HStack spacing={1}>
      {meta && <Box>{meta}</Box>}
      {control}
    </HStack>
  </Flex>
));

const Chevron = () => (
  <Box as="span" className="material-icons-outlined" fontSize="14px" color="gray.300">
    chevron_right
  </Box>
);

// ── Stacked connector icons ───────────────────────────────────────────────────
const ICON_SIZE = 16;
const ICON_STEP = 9;

const StackedConnectorIcons = ({ connectors = [] }) => {
  const items = connectors.slice(0, 3);
  if (items.length === 0) return null;
  const totalWidth = ICON_SIZE + (items.length - 1) * ICON_STEP;
  return (
    <Box position="relative" w={`${totalWidth}px`} h={`${ICON_SIZE}px`} flexShrink={0}>
      {items.map((c, i) => {
        const logoEntry = getConnectorLogo(c.id);
        const bg = logoEntry ? logoEntry.bg : (c.id === 'web_search' ? '#1A73E8' : (c.iconBg || '#888'));
        return (
          <Box
            key={i}
            position="absolute"
            left={`${i * ICON_STEP}px`}
            top={0}
            w={`${ICON_SIZE}px`}
            h={`${ICON_SIZE}px`}
            borderRadius="full"
            bg={bg}
            border="1.5px solid white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={items.length - i}
            title={c.name}
          >
            {logoEntry
              ? <logoEntry.Logo size={9} />
              : <Text fontSize="7px" color="white" fontWeight="800" lineHeight={1}>{c.icon || c.name?.[0] || '?'}</Text>
            }
          </Box>
        );
      })}
    </Box>
  );
};

// ── Sub-menu flyout ───────────────────────────────────────────────────────────
// Portal + position:fixed avoids ALL parent overflow/clip issues.
// isInFlyoutRef (owned by SettingsPopover) lets the controlled Popover
// intercept onClose and block it when the user is interacting with the flyout.
const FLYOUT_WIDTH = 230;

const FlyoutRow = ({
  name, activeSubMenu, onOpen, onClose,
  onFlyoutEnter, onFlyoutLeave,
  rowProps, children,
}) => {
  const isOpen = activeSubMenu === name;
  const rowRef = useRef(null);
  const [coords, setCoords] = useState(null);

  // Calculate coords from the row at interaction time, not in a useEffect.
  // useEffect fires during Chakra's open animation so getBoundingClientRect
  // returns mid-animation values. By the time the mouse reaches a row,
  // the Popover is fully settled and coords are accurate.
  const calculateCoords = useCallback(() => {
    if (rowRef.current) {
      const rect = rowRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 768;
      const preferredLeft = isMobile
        ? rect.left
        : (vw - rect.right >= FLYOUT_WIDTH + 8)
          ? rect.right + 6
          : rect.left - FLYOUT_WIDTH - 6;
      const left = Math.min(
        Math.max(8, preferredLeft),
        Math.max(8, vw - FLYOUT_WIDTH - 8),
      );

      if (isMobile) {
        const top = rect.bottom + 6;
        const maxH = vh - top - 16;
        setCoords({ top, left, maxH, opensDown: true });
        return;
      }

      const bottom = vh - rect.bottom - 4;
      const maxH = rect.bottom - 16; // space available above the row
      setCoords({ bottom, left, maxH, opensDown: false });
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    calculateCoords();
    onOpen(name);
  }, [calculateCoords, name, onOpen]);

  const handleClick = useCallback(() => {
    calculateCoords();
    onOpen(name);
  }, [calculateCoords, name, onOpen]);

  return (
    <Box
      ref={rowRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onClose}
    >
      <MenuRow isActive={isOpen} {...rowProps} onClick={handleClick} />
      {isOpen && coords && (
        <Portal>
          <Box
            position="fixed"
            top={coords.opensDown ? `${coords.top}px` : undefined}
            bottom={coords.opensDown ? undefined : `${coords.bottom}px`}
            left={`${coords.left}px`}
            w={`${FLYOUT_WIDTH}px`}
            maxH={`${Math.max(160, coords.maxH)}px`}
            overflowY="auto"
            bg="white"
            borderRadius="xl"
            boxShadow="lg"
            border="1px solid"
            borderColor="gray.100"
            p={2}
            zIndex={1600}
            onMouseEnter={() => { onFlyoutEnter?.(); onOpen(name); }}
            onMouseLeave={() => { onFlyoutLeave?.(); onClose(); }}
          >
            {children}
          </Box>
        </Portal>
      )}
    </Box>
  );
};


// ── Knowledge popover ─────────────────────────────────────────────────────────
const KnowledgePopover = ({ placement = 'top-start', currentPlanId = null, briefStyle = false, variant = 'context' }) => {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isGitHubBindingOpen, setIsGitHubBindingOpen] = useState(false);
  const [githubBindings, setGitHubBindings] = useState([]);
  const closeTimerRef = useRef(null);
  const isInFlyoutRef = useRef(false);

  const fetchConnectors = useCallback(() => {
    fetchWithAuth('/api/connectors')
      .then(r => r.json())
      .then(data => { if (data.connectors) setConnectors(data.connectors); })
      .catch(() => {});
  }, []);

  const fetchGitHubBindings = useCallback(() => {
    if (!currentPlanId) {
      setGitHubBindings([]);
      return;
    }
    fetchWithAuth(`/api/connectors/plans/${currentPlanId}/bindings`)
      .then(r => r.json())
      .then(data => {
        const bindings = (data.bindings || []).filter(binding => binding.connector_id === 'github');
        setGitHubBindings(bindings);
      })
      .catch(() => setGitHubBindings([]));
  }, [currentPlanId]);

  useEffect(() => {
    fetchConnectors();
    fetchGitHubBindings();

    // OAuth opens in another tab. Refresh connection state when the user
    // returns to Pabs so the menu immediately reflects the completed flow.
    window.addEventListener('focus', fetchConnectors);
    window.addEventListener('focus', fetchGitHubBindings);
    return () => {
      window.removeEventListener('focus', fetchConnectors);
      window.removeEventListener('focus', fetchGitHubBindings);
    };
  }, [fetchConnectors, fetchGitHubBindings]);

  useEffect(() => {
    if (!currentPlanId) return undefined;
    const refreshGitHubBindings = event => {
      if (event.detail?.planId && String(event.detail.planId) !== String(currentPlanId)) return;
      fetchGitHubBindings();
    };
    window.addEventListener('pabs:githubBindingsUpdated', refreshGitHubBindings);
    return () => window.removeEventListener('pabs:githubBindingsUpdated', refreshGitHubBindings);
  }, [currentPlanId, fetchGitHubBindings]);

  useEffect(() => {
    if (activeSubMenu === null) isInFlyoutRef.current = false;
  }, [activeSubMenu]);

  useEffect(() => {
    if (!currentPlanId) return undefined;
    const openGitHubRepositories = event => {
      if (event.detail?.planId && String(event.detail.planId) !== String(currentPlanId)) return;
      setIsOpen(false);
      setActiveSubMenu(null);
      setIsGitHubBindingOpen(true);
    };
    const routePlanId = window.location.pathname.match(/^\/plan\/([^/]+)/)?.[1];
    if (String(routePlanId) === String(currentPlanId) && new URLSearchParams(window.location.search).get('setup') === 'repository') {
      openGitHubRepositories({ detail: { planId: currentPlanId } });
      const url = new URL(window.location.href);
      url.searchParams.delete('setup');
      window.history.replaceState(window.history.state, '', url.toString());
    }
    window.addEventListener('pabs:openGitHubRepositories', openGitHubRepositories);
    return () => window.removeEventListener('pabs:openGitHubRepositories', openGitHubRepositories);
  }, [currentPlanId]);

  const connected  = connectors.filter(c => c.id !== 'web_search' && c.status === 'connected');
  const knowledgeSources = sortConnectorsByAvailability(
    connectors.filter(c => c.id !== 'web_search')
  );
  const githubBindingLabel = githubBindings.length > 0
    ? `${githubBindings[0].display_name?.split('/').pop() || 'Repository'}${githubBindings.length > 1 ? ` +${githubBindings.length - 1}` : ''}`
    : 'Select repository';

  const openSubMenu = useCallback((name) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveSubMenu(name);
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setActiveSubMenu(null), 120);
  }, []);

  const handleClose = useCallback(() => {
    if (isInFlyoutRef.current) return;
    setIsOpen(false);
    setActiveSubMenu(null);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('pabs:walkthroughNudgeInteracted', {
      detail: { key: 'plan_ready_sources' },
    }));
  }, []);

  const connectorListContent = (
              <VStack align="stretch" spacing={0}>
                {knowledgeSources.map(c => {
                  const isConnected = c.status === 'connected';
                  const isDisabled  = c.disabled;
                  const logoEntry   = getConnectorLogo(c.id);
                  const token       = localStorage.getItem('token');
                  const apiBase     = window.location.port === '3000' ? 'http://localhost:3001' : '';
                  const connectAction = `${apiBase}/api/connectors/connect/${c.id}`;
                  return (
                    <Flex
                      key={c.id}
                      align="center" justify="space-between"
                      px={3} py={2} borderRadius="lg"
                      cursor="default"
                      opacity={isDisabled ? 0.55 : 1}
                      _hover={isDisabled ? {} : { bg: 'gray.50' }}
                      textDecoration="none"
                    >
                      <HStack spacing={2}>
                        <Box
                          w="20px" h="20px" borderRadius="4px" flexShrink={0}
                          bg={logoEntry ? logoEntry.bg : (c.iconBg || '#888')}
                          display="flex" alignItems="center" justifyContent="center"
                        >
                          {logoEntry
                            ? <logoEntry.Logo size={10} />
                            : <Text fontSize="7px" color="white" fontWeight="800" lineHeight={1}>{c.name?.[0] || '?'}</Text>
                          }
                        </Box>
                        <Text fontSize="sm" color="gray.700" fontWeight="500">{c.name}</Text>
                      </HStack>
                      {isConnected && c.id === 'github' && currentPlanId ? (
                        <Box
                          as="button"
                          type="button"
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.600"
                          cursor="pointer"
                          px={2}
                          py={1}
                          borderRadius="md"
                          border="none"
                          bg="transparent"
                          _hover={{ color: 'gray.900', bg: 'gray.100' }}
                          onClick={() => {
                            isInFlyoutRef.current = false;
                            setActiveSubMenu(null);
                            setIsOpen(false);
                            setIsGitHubBindingOpen(true);
                          }}
                        >
                          <HStack spacing={1}>
                            <Text as="span" fontSize="xs" fontWeight="600" maxW="92px" isTruncated>
                              {githubBindingLabel}
                            </Text>
                            <Box as="span" className="material-icons-outlined" fontSize="13px">
                              edit
                            </Box>
                          </HStack>
                        </Box>
                      ) : isConnected ? (
                        <Box as="span" className="material-icons-outlined" fontSize="14px" color="green.400">check</Box>
                      ) : isDisabled ? (
                        <Badge fontSize="9px" colorScheme="gray" variant="subtle" borderRadius="full">Soon</Badge>
                      ) : (
                        <Box
                          as="form"
                          action={connectAction}
                          method="get"
                          target="_blank"
                          onMouseDown={event => event.stopPropagation()}
                          onClick={event => event.stopPropagation()}
                        >
                          <input type="hidden" name="token" value={token || ''} />
                          <Box
                            as="button"
                            type="submit"
                            fontSize="xs"
                            fontWeight="600"
                            color="gray.500"
                            cursor="pointer"
                            px={2}
                            py={1}
                            borderRadius="md"
                            _hover={{ color: 'gray.800', bg: 'gray.100' }}
                          >
                            Connect
                          </Box>
                        </Box>
                      )}
                    </Flex>
                  );
                })}
                <Divider borderColor="gray.100" my={1} />
                <Flex
                  px={3} py={2} borderRadius="lg" cursor="pointer"
                  _hover={{ bg: 'gray.50' }} align="center"
                  onClick={() => { navigate('/connectors/knowledge'); setIsOpen(false); }}
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="500">Manage sources</Text>
                </Flex>
              </VStack>
  );

  if (variant === 'row') {
    return (
      <>
      <Popover isOpen={isOpen} onOpen={handleOpen} onClose={handleClose} placement={placement} gutter={8}>
        <PopoverTrigger>
          <Flex align="center" gap={2} cursor="pointer" userSelect="none" _hover={{ opacity: 0.75 }}>
            <Text fontSize="sm" fontWeight="700" color="gray.900">Connected sources</Text>
            {connected.length > 0 && <StackedConnectorIcons connectors={connected} />}
            {connected.length > 0 && <Text fontSize="xs" color="gray.400">{connected.length}</Text>}
            <Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.300">chevron_right</Box>
          </Flex>
        </PopoverTrigger>
        <PopoverContent w="240px" borderRadius="xl" boxShadow="lg" border="1px solid" borderColor="gray.100" _focus={{ outline: 'none' }}>
          <PopoverBody p={2}>{connectorListContent}</PopoverBody>
        </PopoverContent>
      </Popover>
      {currentPlanId && (
        <GitHubPlanBindingModal
          isOpen={isGitHubBindingOpen}
          onClose={() => setIsGitHubBindingOpen(false)}
          planId={currentPlanId}
          planName="this plan"
        />
      )}
      </>
    );
  }

  return (
    <>
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={handleClose} placement={placement} gutter={8}>
      <Tooltip label="Add knowledge" hasArrow placement="top" shouldWrapChildren>
        <PopoverTrigger>
          <Button
            data-walkthrough-anchor="sources"
            leftIcon={<Box as="span" className="material-icons-outlined" fontSize="16px">hub</Box>}
            size={briefStyle ? "sm" : "xs"}
            variant={briefStyle ? "outline" : "ghost"}
            colorScheme="gray"
            aria-label="Add knowledge"
            color="gray.500"
            fontWeight="500"
            borderRadius={briefStyle ? "full" : undefined}
            borderColor={briefStyle ? "gray.300" : undefined}
            px={briefStyle ? 3 : 2}
            rightIcon={briefStyle ? (
              <Box as="span" className="material-icons-outlined" fontSize="16px" color="gray.500">
                expand_more
              </Box>
            ) : undefined}
            _hover={briefStyle
              ? { bg: 'gray.50', borderColor: 'gray.400', color: 'gray.700' }
              : { bg: 'gray.100', color: 'gray.700' }}
          >
            {briefStyle ? (
              <Text fontSize="xs" fontWeight="medium" color="gray.700">
                Context
              </Text>
            ) : 'Context'}
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        w="240px" borderRadius="xl" boxShadow="lg"
        border="1px solid" borderColor="gray.100"
        _focus={{ outline: 'none' }}
        motionProps={{
          variants: {
            enter: { opacity: 1, scale: 1, transition: { duration: 0.15 } },
            exit:  { opacity: 0, scale: 1, transition: { duration: 0.1  } },
          },
        }}
      >
        <PopoverBody p={2}>
          <VStack align="stretch" spacing={0}>

            <SectionLabel>Knowledge Sources</SectionLabel>
            <FlyoutRow
              name="sources"
              activeSubMenu={activeSubMenu}
              onOpen={openSubMenu}
              onClose={scheduleClose}
              onFlyoutEnter={() => { isInFlyoutRef.current = true; }}
              onFlyoutLeave={() => { isInFlyoutRef.current = false; }}
              rowProps={{
                icon: connected.length > 0 ? null : 'hub',
                label: 'Knowledge sources',
                meta: connected.length > 0
                  ? <HStack spacing={1}><StackedConnectorIcons connectors={connected} /><Text fontSize="xs" color="gray.400">{connected.length}</Text></HStack>
                  : null,
                control: <Chevron />,
              }}
            >
              {connectorListContent}
            </FlyoutRow>

          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
    {currentPlanId && (
      <GitHubPlanBindingModal
        isOpen={isGitHubBindingOpen}
        onClose={() => setIsGitHubBindingOpen(false)}
        planId={currentPlanId}
        planName="this plan"
      />
    )}
    </>
  );
};

// ── Settings popover ──────────────────────────────────────────────────────────
const SettingsPopover = ({
  interactionMode, setInteractionMode,
  onAgentSelect, selectedAgents,
  webSearchEnabled,
  isDisabled, isOnBriefPage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [agents, setAgents] = useState([]);
  const [enabledAgents, setEnabledAgents] = useState(new Set());
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const closeTimerRef = useRef(null);
  // True while the mouse is physically inside a Portal flyout panel.
  // Chakra's useOutsideClick fires on Portal clicks — we use this ref to
  // block onClose in that case, so Switch interactions don't kill the Popover.
  const isInFlyoutRef = useRef(false);

  // When the flyout closes for any reason (programmatic or mouse-leave),
  // always reset isInFlyoutRef so the Popover can be closed normally again.
  useEffect(() => {
    if (activeSubMenu === null) {
      isInFlyoutRef.current = false;
    }
  }, [activeSubMenu]);

  useEffect(() => {
    setAgentsLoading(true);
    fetchWithAuth('/api/agents/capabilities')
      .then(r => r.json())
      .then(data => {
        if (data.enabled_agents) {
          setAgents(data.enabled_agents);
          if (!initialized && selectedAgents.length === 0) {
            const all = new Set(data.enabled_agents.map(a => a.id));
            setEnabledAgents(all);
            onAgentSelect?.(data.enabled_agents);
          } else if (selectedAgents.length > 0) {
            setEnabledAgents(new Set(selectedAgents.map(a => a.id || a.agentId)));
          }
          setInitialized(true);
        }
      })
      .catch(() => {})
      .finally(() => setAgentsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialized && selectedAgents.length > 0) {
      setEnabledAgents(new Set(selectedAgents.map(a => a.id || a.agentId)));
    }
  }, [selectedAgents, initialized]);

  const toggleAgent = useCallback((agent, checked) => {
    const next = new Set(enabledAgents);
    checked ? next.add(agent.id) : next.delete(agent.id);
    setEnabledAgents(next);
    onAgentSelect?.(agents.filter(a => next.has(a.id)));
  }, [enabledAgents, agents, onAgentSelect]);

  const openSubMenu = useCallback((name) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveSubMenu(name);
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setActiveSubMenu(null), 120);
  }, []);

  // Intercept Chakra's close request. Block only when mouse is inside an open flyout
  // (Chakra's useOutsideClick fires on Portal clicks, which we must ignore).
  // If no flyout is open, always allow close — isInFlyoutRef can't legitimately be true.
  const handleClose = useCallback(() => {
    if (isInFlyoutRef.current && activeSubMenu !== null) return;
    isInFlyoutRef.current = false;
    setIsOpen(false);
    setActiveSubMenu(null);
  }, [activeSubMenu]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      placement="top-start"
      gutter={8}
    >
      <Tooltip label="Settings" hasArrow placement="top" shouldWrapChildren>
        <PopoverTrigger>
          <IconButton
            icon={<Box as="span" className="material-icons-outlined" fontSize="17px">tune</Box>}
            size="xs" variant="ghost" colorScheme="gray"
            aria-label="Settings"
            color="gray.500"
            _hover={{ bg: 'gray.100', color: 'gray.700' }}
            isDisabled={isDisabled}
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        w="240px" borderRadius="xl" boxShadow="lg"
        border="1px solid" borderColor="gray.100"
        _focus={{ outline: 'none' }}
        motionProps={{
          variants: {
            enter: { opacity: 1, scale: 1, transition: { duration: 0.15 } },
            exit:  { opacity: 0, scale: 1, transition: { duration: 0.1  } },
          },
        }}
      >
        <PopoverBody p={2}>
          <VStack align="stretch" spacing={0}>

            {/* ── Web Search — static indicator, not interactive ── */}
            <SectionLabel>Search</SectionLabel>
            <Flex
              align="center" justify="space-between"
              px={3} py={2} borderRadius="lg"
              userSelect="none"
            >
              <HStack spacing={3}>
                <Box as="span" className="material-icons-outlined" fontSize="16px" color="green.500">
                  travel_explore
                </Box>
                <Text fontSize="sm" color="gray.700" fontWeight="500">Web search</Text>
              </HStack>
              <Box as="span" className="material-icons-outlined" fontSize="16px" color="green.500">
                check
              </Box>
            </Flex>

            {/* ── Chat ── */}
            <SectionLabel>Chat</SectionLabel>

            {/* Mode flyout */}
            {!isOnBriefPage && (
              <FlyoutRow
                name="mode"
                activeSubMenu={activeSubMenu}
                onOpen={openSubMenu}
                onClose={scheduleClose}
                onFlyoutEnter={() => { isInFlyoutRef.current = true; }}
                onFlyoutLeave={() => { isInFlyoutRef.current = false; }}
                rowProps={{
                  icon: 'bolt',
                  label: 'Mode',
                  meta: <Text fontSize="xs" color="gray.400">{interactionMode === 'work' ? 'Work' : 'Advisory'}</Text>,
                  control: <Chevron />,
                }}
              >
                <VStack align="stretch" spacing={0}>
                  {[
                    { value: 'advisory', label: 'Advisory', desc: 'Agents suggest actions and ask before making any changes.' },
                    { value: 'work',     label: 'Work',     desc: 'Agents act immediately without waiting for your approval.' },
                  ].map(m => (
                    <Flex
                      key={m.value}
                      align="center" justify="space-between"
                      px={3} py={2} borderRadius="lg" cursor="pointer"
                      bg={interactionMode === m.value ? 'gray.50' : 'transparent'}
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => { setInteractionMode(m.value); isInFlyoutRef.current = false; setIsOpen(false); setActiveSubMenu(null); }}
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight={interactionMode === m.value ? '600' : '400'} color="gray.800">
                          {m.label}
                        </Text>
                        <Text fontSize="xs" color="gray.400">{m.desc}</Text>
                      </VStack>
                      <Switch
                        size="sm"
                        isChecked={interactionMode === m.value}
                        onChange={() => { setInteractionMode(m.value); isInFlyoutRef.current = false; setIsOpen(false); setActiveSubMenu(null); }}
                        colorScheme="blue"
                        onClick={e => e.stopPropagation()}
                      />
                    </Flex>
                  ))}
                </VStack>
              </FlyoutRow>
            )}

            {/* Agents flyout */}
            <FlyoutRow
              name="agents"
              activeSubMenu={activeSubMenu}
              onOpen={openSubMenu}
              onClose={scheduleClose}
              onFlyoutEnter={() => { isInFlyoutRef.current = true; }}
              onFlyoutLeave={() => { isInFlyoutRef.current = false; }}
              rowProps={{
                icon: 'psychology',
                label: 'Agents',
                meta: <Text fontSize="xs" color="gray.400">{enabledAgents.size} active</Text>,
                control: <Chevron />,
              }}
            >
              <VStack align="stretch" spacing={0}>
                {agentsLoading ? (
                  <Box py={3} textAlign="center"><Spinner size="xs" color="gray.400" /></Box>
                ) : agents.length === 0 ? (
                  <Text fontSize="xs" color="gray.400" px={3} py={2}>No agents available.</Text>
                ) : (
                  agents.map(agent => {
                    const enabled = enabledAgents.has(agent.id);
                    return (
                      <Flex
                        key={agent.id}
                        align="center" justify="space-between"
                        px={3} py={2} borderRadius="lg" cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => toggleAgent(agent, !enabled)}
                      >
                        <VStack align="start" spacing={0} flex={1} mr={3} minW={0}>
                          <Text
                            fontSize="sm"
                            fontWeight={enabled ? '600' : '400'}
                            color={enabled ? 'gray.800' : 'gray.400'}
                            noOfLines={1}
                          >
                            {agent.name}
                          </Text>
                          {agent.brief_description && (
                            <Text fontSize="10px" color="gray.400" noOfLines={2} lineHeight="1.3">
                              {agent.brief_description}
                            </Text>
                          )}
                        </VStack>
                        <Switch
                          isChecked={enabled}
                          onChange={e => { e.stopPropagation(); toggleAgent(agent, e.target.checked); }}
                          colorScheme="teal" size="sm"
                          onClick={e => e.stopPropagation()}
                          flexShrink={0}
                        />
                      </Flex>
                    );
                  })
                )}
              </VStack>
            </FlyoutRow>

          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
const ChatFooterBar = ({
  interactionMode,
  setInteractionMode,
  onAgentSelect,
  selectedAgents = [],
  loading,
  input,
  onSend,
  onAttachClick,
  hasAttachments = false,
  attachmentsUploading = false,
  isOnBriefPage,
  currentPlanId,
}) => {
  const [webSearchEnabled] = useState(true);

  return (
    <Flex justify="space-between" align="center" px={2} py={2}>
      <HStack spacing={1}>
        <KnowledgePopover currentPlanId={currentPlanId} />
        <Tooltip label="Attach a .txt or .csv file for Pabs to read and analyze">
          <IconButton
            icon={<Box as="span" className="material-icons-outlined" fontSize="18px">attach_file</Box>}
            onClick={onAttachClick}
            size="sm"
            variant={hasAttachments ? "solid" : "ghost"}
            colorScheme={hasAttachments ? "blue" : "gray"}
            borderRadius="xl"
            isDisabled={loading}
            aria-label="Attach file"
          />
        </Tooltip>
        <SettingsPopover
          interactionMode={interactionMode}
          setInteractionMode={setInteractionMode}
          onAgentSelect={onAgentSelect}
          selectedAgents={selectedAgents}
          webSearchEnabled={webSearchEnabled}
          isDisabled={loading}
          isOnBriefPage={isOnBriefPage}
        />
      </HStack>

      <IconButton
        icon={<ArrowForwardIcon />}
        onClick={() => onSend?.()}
        colorScheme="blue"
        size="sm"
        borderRadius="xl"
        isLoading={loading}
        isDisabled={(!input?.trim() && !hasAttachments) || attachmentsUploading || loading}
        aria-label="Send message"
      />
    </Flex>
  );
};

export { KnowledgePopover };
export default ChatFooterBar;
